/**
 * O prazo de 24 h à vista (05/10): quando vence o próximo, e o que já venceu hoje.
 *
 * Os dois são relidos **junto com a fila** — `atualizadoEm` muda quando a lista chega —, e
 * não num relógio próprio: é a fila que sabe quando o motor de prazos tirou alguém dela, e
 * um número de perda que mudasse fora de compasso com a lista seria a tela dizendo duas
 * coisas ao mesmo tempo. A perda também é relida a cada minuto, porque a SENATRAN não vigia
 * a fila sozinha e o vencimento acontece sem ninguém clicar.
 */
import { useEffect, useState } from "react";

import { api } from "../api/cliente";
import type { PerdidosHoje, ProximosPrazos } from "../api/tipos";
import { repetirComAbaVisivel } from "./repetirComAbaVisivel";

/** De quanto em quanto tempo a perda do dia é relida sem a fila mudar. */
export const RELEITURA_DA_PERDA_MS = 60_000;

/** O próximo vencimento de cada prazo no universo da tela, relido junto com a fila. */
export function useProximosPrazos(
  filtros: string,
  atualizadoEm: string | null,
  universo: "senatran" | "na_operacao" = "senatran",
): ProximosPrazos | null {
  const [proximos, definirProximos] = useState<ProximosPrazos | null>(null);
  useEffect(() => {
    // Só depois que a fila chegou: antes, `atualizadoEm` nulo e depois preenchido disparavam
    // a mesma consulta duas vezes a cada abertura da tela.
    if (atualizadoEm === null) return;
    let vivo = true;
    const p = new URLSearchParams(filtros);
    p.set("universo", universo);
    api<ProximosPrazos>(`/tollvision/prazos/proximos?${p.toString()}`)
      .then((r) => vivo && definirProximos(r))
      .catch(() => vivo && definirProximos(null));
    return () => {
      vivo = false;
    };
  }, [filtros, atualizadoEm, universo]);
  return proximos;
}

/**
 * Quantos perderam o prazo de 24 h hoje e quanto valiam — a organização inteira (no recorte
 * de concessão de quem olha), como "Aprovados hoje": é o placar do dia, não um filtro da
 * fila aberta. `null` enquanto não chegou ou se falhou: a pílula some, em vez de dizer zero.
 */
export function usePerdidosHoje(
  atualizadoEm: string | null,
): PerdidosHoje | null {
  const [perdidos, definirPerdidos] = useState<PerdidosHoje | null>(null);
  useEffect(() => {
    // Como os próximos prazos: só depois que a fila chegou. Antes, a abertura da tela pedia
    // a perda do dia duas vezes — com `atualizadoEm` nulo e logo depois preenchido.
    if (atualizadoEm === null) return;
    let vivo = true;
    const ler = () =>
      api<PerdidosHoje>("/tollvision/prazos/perdidos-hoje")
        .then((r) => vivo && definirPerdidos(r))
        .catch(() => vivo && definirPerdidos(null));
    void ler();
    const parar = repetirComAbaVisivel(() => void ler(), RELEITURA_DA_PERDA_MS);
    return () => {
      vivo = false;
      parar();
    };
  }, [atualizadoEm]);
  return perdidos;
}
